/**
 * LimitationsWithCitations Component (Simplified for Normalized Schema)
 *
 * Displays limitations with proper structure:
 * - Title (bold)
 * - Description with inline citation pills
 */

import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import colors from '../../lib/colors';
import { parseTextWithCitationsAtEnd } from '../../utils/citationParser';
import { parseLimitationContent } from '../../utils/limitationParser';
import type { LimitationItem } from './types';
import LaTeXText from '../LaTeXText';
import { interRegular, interSemiBold } from "../../lib/fonts";
import { Trans } from '@lingui/react/macro';

interface LimitationsWithCitationsProps {
    limitations: LimitationItem[];
    fontsLoaded: boolean;
    onHoverChange?: (isHovered: boolean, tooltipText?: string) => void;
}

export default function LimitationsWithCitations({
    limitations,
    fontsLoaded,
    onHoverChange,
}: LimitationsWithCitationsProps) {
    // Track which limitation is currently hovered
    const [hoveredLimitationIndex, setHoveredLimitationIndex] = React.useState<number | null>(null);
    const [tooltipText, setTooltipText] = React.useState<string | null>(null);

    // Guard against undefined or empty limitations
    if (!limitations || limitations.length === 0) {
        return (
            <Text style={[styles.limitationDescription, fontsLoaded && interRegular]}><Trans id="papers.limitationswithcitations.noLimitationsIdentified" comment="UI copy in components/papers/LimitationsWithCitations.tsx">
                No limitations identified.
            </Trans></Text>
        );
    }

    return (
        <>
            {limitations.map((limitation, index) => {
                // Create hover callback for this specific limitation
                const handleLimitationHover = (isHovered: boolean, text?: string) => {
                    setHoveredLimitationIndex(isHovered ? index : null);
                    setTooltipText(isHovered && text ? text : null);
                    // Also notify parent if needed
                    if (onHoverChange) {
                        onHoverChange(isHovered, text);
                    }
                };

                // Parse description text with citations, separating text from pills
                const { pills } = parseTextWithCitationsAtEnd(
                    limitation.description,
                    limitation.citations,
                    handleLimitationHover,
                    [styles.limitationDescription, fontsLoaded && interRegular],
                    fontsLoaded
                );

                const blocks = parseLimitationContent(limitation);

                // Check if this limitation is hovered
                const isHovered = hoveredLimitationIndex === index;

                return (
                    <View key={index} style={[styles.limitationItem, isHovered && styles.limitationItemElevated]}>
                        {blocks.map((block, blockIndex) => (
                            <View key={blockIndex} style={blockIndex > 0 && styles.recoveredItem}>
                                {block.title ? (
                                    <LaTeXText
                                        text={block.title}
                                        style={[styles.limitationTitle, fontsLoaded && interSemiBold]}
                                        fontsLoaded={fontsLoaded}
                                    />
                                ) : null}
                                {block.description ? (
                                    <LaTeXText
                                        text={block.description}
                                        style={[styles.limitationDescription, fontsLoaded && interRegular]}
                                        fontsLoaded={fontsLoaded}
                                    />
                                ) : null}
                            </View>
                        ))}
                        {/* Citation offsets refer to the original LLM response. Keep
                            sources with the original row, not guessed recovered items. */}
                        {pills.length > 0 && (
                            <Text style={[styles.limitationDescription, fontsLoaded && interRegular]}>
                                {pills}
                            </Text>
                        )}

                        {isHovered && tooltipText && Platform.OS === 'web' && (
                            <View style={styles.limitationTooltip}>
                                <LaTeXText
                                    text={tooltipText}
                                    style={[styles.tooltipText, fontsLoaded && interRegular]}
                                    fontsLoaded={fontsLoaded}
                                />
                            </View>
                        )}
                    </View>
                );
            })}
        </>
    );
}

const styles = StyleSheet.create({
    limitationItem: {
        marginBottom: 16,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#f3f4f6',
        position: 'relative',
    },
    recoveredItem: {
        marginTop: 16,
    },
    limitationItemElevated: {
        zIndex: 1000,
    },
    limitationTitle: {
        fontSize: 15,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 8,
    },
    limitationDescription: {
        fontSize: 15,
        color: colors.text700,
        lineHeight: 22,
    },
    limitationTooltip: {
        position: 'relative',
        left: 0,
        right: 0,
        backgroundColor: colors.text900,
        borderRadius: 8,
        padding: 12,
        marginTop: 12,
        zIndex: 99999999,
        pointerEvents: 'none',
        ...(Platform.OS === 'web' && {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        } as any),
    },
    tooltipText: {
        fontSize: 14,
        color: '#ffffff',
        lineHeight: 20,
    },
});
